import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowUpDown, ArrowUp, ArrowDown, TrendingUp, TrendingDown,
  ChevronRight, Trophy, AlertTriangle, DollarSign, Coins, Percent, Filter,
} from 'lucide-react';
import { EmptyState } from '../components/mailer/atoms';
import DateRangePicker, { rangeToIsoParams } from '../components/mailer/DateRangePicker';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/mailer/dashboard`;

// Status from drop date — Active <30d, Recent 30-90d, Closed >90d
function campaignStatus(dropIso) {
  if (!dropIso) return { key: 'unknown', label: 'Unknown', cls: 'bg-neutral-100 text-neutral-500 border-neutral-200' };
  const days = (Date.now() - new Date(dropIso).getTime()) / 86_400_000;
  if (days <= 30) return { key: 'active',  label: 'Active',  cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
  if (days <= 90) return { key: 'recent',  label: 'Recent',  cls: 'bg-sky-50 text-sky-700 border-sky-200' };
  return { key: 'closed', label: 'Closed',  cls: 'bg-neutral-100 text-neutral-500 border-neutral-200' };
}

function fmtCurrency(n) { return `$${Math.round(Number(n || 0)).toLocaleString('en-US')}`; }
function fmtPct(n, digits = 1) { return `${Number(n || 0).toFixed(digits)}%`; }

// ROI bar normalised against the best campaign so the strongest performer
// pegs at 100%. Anything below zero ROI shows a thin red bar.
function ROIBar({ value, max }) {
  const v = Number(value || 0);
  const pos = Math.max(0, v);
  const width = max > 0 ? Math.min(100, (pos / max) * 100) : 0;
  const color = v < 0 ? '#dc2626' : v < 100 ? '#f59e0b' : v < 500 ? '#0d9488' : '#16a34a';
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-20 rounded-full bg-neutral-100 dark:bg-neutral-800 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${Math.max(width, 3)}%`, background: color }} />
      </div>
      <span className={cn('text-[11.5px] font-bold tabular-nums', v < 0 ? 'text-red-600 dark:text-red-400' : 'text-neutral-900 dark:text-neutral-100')}>
        {v >= 0 ? '+' : ''}{fmtPct(v, v >= 1000 ? 0 : 1)}
      </span>
    </div>
  );
}

// Color-code response rate consistently with the dashboard:
//   ≥ 0.75%        green
//   0.50% – 0.74%  amber
//   < 0.50%        red
function ConvPct({ value }) {
  const v = Number(value || 0);
  const cls = v >= 0.75 ? 'text-emerald-600' : v >= 0.5 ? 'text-amber-600' : 'text-red-600';
  return <span className={cn('tabular-nums font-bold', cls)}>{fmtPct(v, 2)}</span>;
}

const SORT_DEFAULT = { key: 'drop_date', dir: 'desc' };

// Render drop_date as MM-DD-YYYY (US convention). Accepts either an ISO
// "YYYY-MM-DD" string from the backend aggregation or any Date-parseable
// value. Falls back to the original string if we can't parse it so we
// never blank out a populated cell.
function fmtDropDate(v) {
  if (!v) return '—';
  const s = String(v);
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[2]}-${iso[3]}-${iso[1]}`;
  const d = new Date(s);
  if (!isNaN(d)) {
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${mm}-${dd}-${d.getFullYear()}`;
  }
  return s;
}

export default function MailerCampaigns() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState(SORT_DEFAULT);
  const [sourceFilter, setSourceFilter] = useState('all');
  // Date filter — shared `DateRangePicker` (split-panel UI matching the
  // Dashboard and Spend tabs).
  const [rangeId, setRangeId] = useState('all');
  const [customRange, setCustomRange] = useState({ from: null, to: null });

  const params = useMemo(() => rangeToIsoParams({ rangeId, customRange }), [rangeId, customRange]);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/campaigns`, { params })
      .then((r) => setRows(r.data || []))
      .finally(() => setLoading(false));
  }, [params]);

  // Distinct source values for the filter chip strip.
  const sources = useMemo(() => {
    const set = new Set(rows.map((r) => r.lead_source).filter(Boolean));
    return ['all', ...Array.from(set).sort()];
  }, [rows]);

  const filtered = useMemo(() => {
    if (sourceFilter === 'all') return rows;
    return rows.filter((r) => r.lead_source === sourceFilter);
  }, [rows, sourceFilter]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    list.sort((a, b) => {
      const av = a[sort.key], bv = b[sort.key];
      if (typeof av === 'number' && typeof bv === 'number') return sort.dir === 'asc' ? av - bv : bv - av;
      return sort.dir === 'asc'
        ? String(av || '').localeCompare(String(bv || ''))
        : String(bv || '').localeCompare(String(av || ''));
    });
    return list;
  }, [filtered, sort]);

  // Best / worst / aggregate metrics for the hero strip.
  const hero = useMemo(() => {
    if (!rows.length) return null;
    const sorted = [...rows].sort((a, b) => (b.roi || 0) - (a.roi || 0));
    const best = sorted[0];
    const worst = sorted[sorted.length - 1];
    const cost = rows.reduce((s, r) => s + (r.total_cost || 0), 0);
    const rev  = rows.reduce((s, r) => s + (r.total_revenue || 0), 0);
    const avgROI = rows.reduce((s, r) => s + (r.roi || 0), 0) / rows.length;
    const maxROI = Math.max(...rows.map((r) => Math.max(r.roi || 0, 0)));
    return { best, worst, cost, rev, net: rev - cost, avgROI, maxROI };
  }, [rows]);

  const setSortCol = (key) => setSort((p) =>
    p.key === key ? { key, dir: p.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' },
  );

  const drillIntoCampaign = (campaign) => {
    if (!campaign) return;
    nav(`/mailer/drilldown?campaign=${encodeURIComponent(campaign)}`);
  };

  return (
    <div className="px-6 lg:px-10 py-8 max-w-[1400px] mx-auto space-y-5" data-testid="mailer-campaigns-page">
      {/* Subhead — the page title lives on the MailerLayout header above. */}
      <div>
        <h2 className="text-[15px] font-bold tracking-tight text-neutral-900 dark:text-neutral-100">Campaign Performance</h2>
        <p className="text-[12.5px] text-neutral-500 dark:text-neutral-400 mt-1">
          Per-campaign economics. Sort or click any row to drill into the conversion path.
        </p>
      </div>

      {/* Hero KPI strip */}
      {hero && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <HeroCard
            label="Best Campaign"
            value={hero.best?.campaign_name || '—'}
            valueKind="text"
            sub={hero.best ? `${fmtCurrency(hero.best.total_revenue)} · ${hero.best.funded || 0} funded` : ''}
            icon={Trophy}
            tone="gold"
            onClick={() => drillIntoCampaign(hero.best?.campaign_name)}
            testId="hero-best"
          />
          <HeroCard
            label="Worst Campaign"
            value={hero.worst?.campaign_name || '—'}
            valueKind="text"
            sub={hero.worst ? `${fmtCurrency(hero.worst.total_cost)} spent · ${hero.worst.funded || 0} funded` : ''}
            icon={AlertTriangle}
            tone="rose"
            onClick={() => drillIntoCampaign(hero.worst?.campaign_name)}
            testId="hero-worst"
          />
          <HeroCard
            label="Total Spend"
            value={fmtCurrency(hero.cost)}
            sub={`Across ${rows.length} drops`}
            icon={Coins}
            tone="sky"
            testId="hero-spend"
          />
          <HeroCard
            label="Net Revenue"
            value={fmtCurrency(hero.net)}
            sub={`${fmtCurrency(hero.rev)} − ${fmtCurrency(hero.cost)}`}
            icon={DollarSign}
            tone={hero.net >= 0 ? 'emerald' : 'rose'}
            testId="hero-net"
          />
        </div>
      )}

      {/* Filter chips — always visible so the LO can see all available */}
      {/* sources at a glance (Conventional / FHA / VA mailer, etc.) even */}
      {/* when only one source has been uploaded so far.                  */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {sources.length > 1 ? (
          <div className="flex items-center gap-2 flex-wrap" data-testid="campaign-source-filter">
            <span className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.18em] font-bold text-neutral-400">
              <Filter className="h-3 w-3" /> Source
            </span>
            {sources.map((s) => {
              const count = s === 'all' ? rows.length : rows.filter((r) => r.lead_source === s).length;
              return (
                <button
                  key={s}
                  data-testid={`campaign-source-${s.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => setSourceFilter(s)}
                  className={cn(
                    'h-8 px-3.5 rounded-full text-[11.5px] font-semibold transition border inline-flex items-center gap-1.5',
                    sourceFilter === s
                      ? 'bg-[hsl(var(--kf-gold))] text-neutral-900 border-[hsl(var(--kf-gold-deep))]/40 ring-1 ring-[hsl(var(--kf-gold-deep))]/30'
                      : 'bg-white dark:bg-neutral-950 text-neutral-600 dark:text-neutral-300 hairline hover:border-[hsl(var(--kf-gold))] hover:text-[hsl(var(--kf-gold-deep))]',
                  )}
                >
                  {s === 'all' ? 'All' : s}
                  <span className={cn(
                    'tabular-nums text-[10px] font-bold px-1.5 py-0.5 rounded-full',
                    sourceFilter === s ? 'bg-neutral-900 text-white' : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400',
                  )}>{count}</span>
                </button>
              );
            })}
          </div>
        ) : <span />}
        <DateRangePicker
          value={{ rangeId, customRange }}
          onApply={(v) => { setRangeId(v.rangeId); setCustomRange(v.customRange); }}
          testIdPrefix="campaigns-daterange"
        />
      </div>

      {/* Table card */}
      <div className="rounded-2xl border hairline bg-white dark:bg-neutral-950 overflow-hidden" data-testid="mailer-campaigns-table">
        {loading ? (
          <div className="px-5 py-16 text-center text-neutral-400 dark:text-neutral-500 text-[12px] uppercase tracking-wider font-bold">Loading campaigns…</div>
        ) : !sorted.length ? (
          <EmptyState title="No campaigns yet" subtitle="Upload a CSV via the Mailer Upload tab to start tracking campaign economics." />
        ) : (
          <table className="w-full text-[12.5px]">
            <thead className="text-left text-[10px] uppercase tracking-wider text-neutral-400 dark:text-neutral-500 font-bold border-b hairline bg-neutral-50/40 dark:bg-neutral-900/40">
              <tr>
                <ColHeader col="lead_source"   sort={sort} setSort={setSortCol} width="14%">Source</ColHeader>
                <ColHeader col="campaign_name" sort={sort} setSort={setSortCol} width="24%">Campaign</ColHeader>
                <ColHeader col="drop_date"     sort={sort} setSort={setSortCol} width="12%">Drop Date</ColHeader>
                <ColHeader col="total_mailed"  sort={sort} setSort={setSortCol} align="right" width="10%">Mailed</ColHeader>
                <ColHeader col="response_rate" sort={sort} setSort={setSortCol} align="right" width="10%">Resp %</ColHeader>
                <ColHeader col="submissions"   sort={sort} setSort={setSortCol} align="right" width="8%">Subs</ColHeader>
                <ColHeader col="funded"        sort={sort} setSort={setSortCol} align="right" width="8%">Funded</ColHeader>
                <ColHeader col="total_cost"    sort={sort} setSort={setSortCol} align="right" width="7%">Cost</ColHeader>
                <ColHeader col="total_revenue" sort={sort} setSort={setSortCol} align="right" width="7%">Revenue</ColHeader>
                <th className="px-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 dark:divide-neutral-900">
              {sorted.map((r) => {
                const respRate = r.response_rate ?? (r.total_mailed ? (r.responses / r.total_mailed) * 100 : 0);
                return (
                  <tr
                    key={`${r.campaign_name}-${r.drop_date}`}
                    onClick={() => drillIntoCampaign(r.campaign_name)}
                    className="hover:bg-neutral-50/80 dark:hover:bg-white/[0.03] transition-colors cursor-pointer group"
                    data-testid={`campaign-row-${r.campaign_name}`}
                  >
                    <td className="px-3 py-3.5 text-neutral-700 dark:text-neutral-300">{r.lead_source || '—'}</td>
                    <td className="px-3 py-3.5">
                      <div className="font-bold text-neutral-900 dark:text-neutral-100 truncate" title={r.campaign_name}>{r.campaign_name}</div>
                    </td>
                    <td className="px-3 py-3.5">
                      <div className="text-neutral-700 dark:text-neutral-300 text-[12.5px] font-semibold tabular-nums">{fmtDropDate(r.drop_date)}</div>
                    </td>
                    <td className="px-3 py-3.5 text-right tabular-nums text-neutral-900 dark:text-neutral-100 font-semibold">{(r.total_mailed || 0).toLocaleString()}</td>
                    <td className="px-3 py-3.5 text-right"><ConvPct value={respRate} /></td>
                    <td className="px-3 py-3.5 text-right tabular-nums text-neutral-900 dark:text-neutral-100">{(r.submissions || 0).toLocaleString()}</td>
                    <td className="px-3 py-3.5 text-right">
                      <span className={cn('tabular-nums font-bold', (r.funded || 0) > 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-neutral-400 dark:text-neutral-600')}>
                        {r.funded || 0}
                      </span>
                    </td>
                    <td className="px-3 py-3.5 text-right tabular-nums text-neutral-700 dark:text-neutral-300">{fmtCurrency(r.total_cost)}</td>
                    <td className="px-3 py-3.5 text-right tabular-nums text-neutral-900 dark:text-neutral-100 font-bold">{fmtCurrency(r.total_revenue)}</td>
                    <td className="pr-3 pl-1 text-neutral-300 dark:text-neutral-600 group-hover:text-neutral-900 dark:group-hover:text-neutral-100">
                      <ChevronRight className="h-4 w-4" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ---- Helpers ------------------------------------------------------------

function HeroCard({ label, value, valueKind = 'num', sub, icon: Icon, tone = 'neutral', onClick, testId }) {
  // Accent palette — matches MailerDashboard KpiTile so the two pages read
  // as one cohesive system. `bg`/`fg` drive the circular icon ring; `bar`
  // drives the bottom progress accent.
  const TONE = {
    gold:    { bg: 'bg-amber-50',   fg: 'text-amber-600',   bar: '#f59e0b' },
    rose:    { bg: 'bg-rose-50',    fg: 'text-rose-600',    bar: '#fb7185' },
    emerald: { bg: 'bg-emerald-50', fg: 'text-emerald-600', bar: '#10b981' },
    sky:     { bg: 'bg-sky-50',     fg: 'text-sky-600',     bar: '#0ea5e9' },
    neutral: { bg: 'bg-neutral-100',fg: 'text-neutral-700', bar: '#a3a3a3' },
  };
  const accent = TONE[tone] || TONE.neutral;
  const clickable = !!onClick;
  // Campaign names can be long — drop the type size and rely on line-clamp.
  const isText = valueKind === 'text';
  return (
    <div
      onClick={onClick}
      data-testid={testId}
      className={cn(
        'relative rounded-2xl border hairline bg-white dark:bg-neutral-950 px-4 py-3.5 transition overflow-hidden',
        clickable
          ? 'cursor-pointer hover:border-neutral-300 dark:hover:border-neutral-700 hover:shadow-[0_12px_32px_-14px_rgba(0,0,0,0.10)]'
          : 'hover:border-neutral-300 dark:hover:border-neutral-700 hover:shadow-[0_12px_32px_-14px_rgba(0,0,0,0.10)]',
      )}
    >
      <div className="flex flex-col items-center">
        <div className={cn('h-10 w-10 rounded-full flex items-center justify-center', accent.bg)}>
          <Icon className={cn('h-[18px] w-[18px]', accent.fg)} strokeWidth={2.2} />
        </div>
        <div className="mt-2.5 text-[9.5px] uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-400 font-bold leading-tight">
          {label}
        </div>
        <div
          className={cn(
            'display font-bold tracking-tight text-neutral-900 dark:text-neutral-100 mt-1.5 leading-tight text-center w-full',
            isText
              ? 'text-[13.5px] line-clamp-2 px-1 min-h-[34px] flex items-center justify-center'
              : 'text-[24px] leading-none tabular-nums',
          )}
          title={isText ? value : undefined}
        >
          {value}
        </div>
      </div>
      <div className="mt-3 flex items-center justify-center text-[10px]">
        <span className="text-neutral-400 dark:text-neutral-500 font-semibold uppercase tracking-wider truncate max-w-full">
          {sub || '—'}
        </span>
      </div>
      {/* Bottom progress accent — solid tone keyed to the icon ring */}
      <div className="absolute left-4 right-4 bottom-2 h-[3px] rounded-full bg-neutral-100 dark:bg-neutral-800 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: '62%', background: accent.bar }} />
      </div>
    </div>
  );
}

function ColHeader({ children, col, sort, setSort, align = 'left', width }) {
  const active = sort?.key === col;
  return (
    <th
      onClick={() => setSort(col)}
      style={{ width }}
      className={cn(
        'px-3 py-2.5 cursor-pointer select-none hover:text-neutral-700',
        align === 'right' && 'text-right',
      )}
    >
      <span className={cn('inline-flex items-center gap-1', align === 'right' && 'justify-end w-full')}>
        {children}
        {active
          ? (sort.dir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)
          : <ArrowUpDown className="h-3 w-3 opacity-40" />}
      </span>
    </th>
  );
}
